import {Component, DestroyRef, inject} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {finalize} from 'rxjs';
import {NzFormModule} from 'ng-zorro-antd/form';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzDescriptionsModule} from 'ng-zorro-antd/descriptions';
import {NZ_MODAL_DATA, NzModalRef} from 'ng-zorro-antd/modal';
import {NzNotificationService} from 'ng-zorro-antd/notification';
import {AuthFlowService} from '../../../core/services/auth-flow.service';
import {EMAIL_VALIDATORS} from '../../../shared/email-validator';

export interface SetEmailModalData {
  userId: string;
  username: string;
  email?: string | null;
}

@Component({
  selector: 'app-set-email-modal',
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    NzFormModule,
    NzInputModule,
    NzDescriptionsModule,
  ],
  templateUrl: './set-email-modal.html',
  styleUrl: './set-email-modal.css',
})
export class SetEmailModal {
  private readonly translate = inject(TranslateService);
  private readonly _data = inject<SetEmailModalData>(NZ_MODAL_DATA);
  private readonly _modalRef = inject(NzModalRef);
  private readonly _authFlow = inject(AuthFlowService);
  private readonly _notification = inject(NzNotificationService);
  private readonly _destroyRef = inject(DestroyRef);

  readonly username = this._data.username;

  readonly emailControl = new FormControl(this._data.email ?? '', {
    nonNullable: true,
    validators: EMAIL_VALIDATORS,
  });

  constructor() {
    this.emailControl.statusChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._modalRef.updateConfig({nzOkDisabled: this.emailControl.invalid}));
  }

  handleOk(): void {
    if (this.emailControl.invalid) {
      this.emailControl.markAsTouched();
      return;
    }

    this._modalRef.updateConfig({nzOkLoading: true, nzCancelDisabled: true});

    this._authFlow.setManagedUserEmail(this._data.userId, this.emailControl.value)
      .pipe(finalize(() => this._modalRef.updateConfig({nzOkLoading: false})), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._modalRef.close(true);
          this._notification.success(this.translate.instant('common.success'), this.translate.instant('adminUsers.emailChanged'));
        },
        error: () => {
          this._modalRef.updateConfig({nzCancelDisabled: false});
        }
      });
  }
}
